import React, { useEffect, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined, UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer, Row, Col } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readTripHoliday,
    createTripHoliday,
    updateTripHoliday
} from '../actions/entity.trip.holiday'
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';
import Editor from './components/editor.field';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function TripHoliday() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [entityHolidayTitle, setTripHolidayTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredTripSupportAllLanguages = currentEntity && currentEntity.requiredTripSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [holidayData, setHolidayData] = useState(null);

    const showMediaDrawer = (fieldName) => {
        console.log(fieldName);
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            let formValues = form.getFieldsValue();
            // Use dot to update partial values
            newValue = dot.dot(newValue);
            formValues = dot.dot(formValues);
            newValue = { ...formValues, ...newValue };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredTripSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (holidayData && typeof holidayData.languages !== 'undefined' && Array.isArray(holidayData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && holidayData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
        }
    }, [currentEntity, holidayData]);
    const languageOptions = !requiredTripSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('tripHoliday.message.read-only'));
            return;
        }
        // Loop languages and check its features and images
        addedLanguages.forEach(language => {
            if (typeof values[language.value].features === 'undefined') {
                values[language.value].features = [];
            }
            if (typeof values[language.value].images === 'undefined') {
                values[language.value].images = [];
            }
        });

        if (typeof params.holidayId !== 'undefined') {
            // Update the existing trip holiday
            dispatch(updateTripHoliday(params.entityId, params.holidayId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripHolidayTitle(t('tripHoliday.title.edit'));
                    form.setFieldsValue(response.result);
                    setHolidayData(response.result);
                }
                message.success(t('tripHoliday.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createTripHoliday(params.entityId, values)).then((data) => {
                message.success(t('tripHoliday.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/trip-holiday/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/trip-holidays`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.holidayId !== 'undefined') {
            setTripHolidayTitle(t('tripHoliday.title.edit'));
            dispatch(readTripHoliday(params.entityId, params.holidayId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripHolidayTitle(t('tripHoliday.title.edit'));
                    form.setFieldsValue(response.result);
                    setHolidayData(response.result);
                } else {
                    message.error(t('tripHoliday.message.error-reading'));
                    navigate(`/entity/${params.entityId}/trip-holidays`);
                }
            }).catch(e => {
                message.error(t('tripHoliday.message.error-reading'));
                navigate(`/entity/${params.entityId}/trip-holidays`);
            });
        } else {
            setTripHolidayTitle(t('tripHoliday.title.new'));
        }
    }, [params.holidayId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/trip-holidays`}>{t('tripHoliday.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.holidayId ? t('tripHoliday.actions.update') : t('tripHoliday.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-holiday"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/trip-holidays`)}
                            title={entityHolidayTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('tripHoliday.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripHoliday.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripHoliday.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('tripHoliday.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('tripHoliday.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('tripHoliday.field.slug.note')}</div>
                            </Form.Item>
                            {!requiredTripSupportAllLanguages && <Form.Item
                                label={t('tripHoliday.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripHoliday.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('tripHoliday.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('tripHoliday.field.name.label')}
                                            name={[language.value, 'name']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripHoliday.field.name.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripHoliday.field.name.placeholder')} />
                                        </Form.Item>
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default TripHoliday;